import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
    Plus, Edit, Trash2, Eye, UserPlus, UserMinus,
    KeyRound, Users, CalendarClock, AlertTriangle, ArrowUpDown,
    Loader2, Download, ShieldCheck, RefreshCw, Settings2,
    Upload, FileSpreadsheet, Check
} from 'lucide-react';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '../../components/ui/dropdown-menu';
import { licenseService, License, LicenseType, LicenseListFilters, LicenseStatistics } from '../../services/licenseService';
import { authService } from '../../services/authService';
import { masterService, Lookup, Vendor, LicensePlan } from '../../services/masterService';
import { departmentsService, Department } from '../../services/lookupService';
import { userService, User } from '../../services/userService'; // Might need for assignment logic if lifted up
import { useToast } from '../../context/ToastContext';
import { exportToCsv } from '../../lib/exportCsv';
import ConfirmModal from '../../components/Common/ConfirmModal';
import ActionDropdown, { ActionItem } from '../../components/Common/ActionDropdown';
import { PageHeader } from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useCurrency } from '../../context/CurrencyContext';
import { formatPercent } from '../../lib/formatNumber';
import { cn } from '../../lib/utils';
import LicenseForm from './LicenseForm';
import LicenseDetails from './LicenseDetails';
import LicenseImportPreview, { ImportPreviewRow, ImportPreviewSummary } from './LicenseImportPreview';
import { Pagination } from '../../components/shared/Pagination';
import UserSelect from '../../components/shared/UserSelect';
import { FormField } from '../../components/shared/FormField';
import { useForm } from '../../hooks/useForm';
import {
    LicenseAdvancedFilters,
    LicenseAdvancedFilterState,
    EMPTY_LICENSE_FILTERS,
} from './LicenseAdvancedFilters';
import { MultiSelectOption } from '../../components/shared/MultiSelect';
import { useManagementTableState } from '../../hooks/useManagementTableState';
import { FilterButton } from '../../components/shared/FilterButton';
import { ListTabBar, ListToolbar } from '../../components/shared/ListTabBar';
import { FilterChipsBar, FilterChip } from '../../components/shared/FilterChipsBar';
import { TableLoadingBar, TABLE_DIM_CLASS } from '../../components/shared/TableLoadingBar';


/** Table/card-list empty state — distinguishes "nothing in the system yet"
 * from "this search/filter narrowed the results to zero". */
const EmptyLicensesState: React.FC<{ hasFilters: boolean; onClearFilters: () => void }> = ({ hasFilters, onClearFilters }) => (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-center text-sm text-muted-foreground">
        <p>{hasFilters ? 'No licenses match the selected filters.' : 'No licenses found.'}</p>
        {hasFilters && (
            <Button type="button" variant="outline" size="sm" className="cursor-pointer" onClick={onClearFilters}>
                Clear Filters
            </Button>
        )}
    </div>
);

const LicenseManagement: React.FC = () => {
    const [licenses, setLicenses] = useState<License[]>([]);
    const [totalLicenses, setTotalLicenses] = useState(0);
    const [stats, setStats] = useState<LicenseStatistics | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [licensePlans, setLicensePlans] = useState<LicensePlan[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [lookups, setLookups] = useState<Record<string, Lookup[]>>({});
    const [loading, setLoading] = useState(true);
    const [listLoading, setListLoading] = useState(false);
    const [currentUser, setCurrentUser] = useState<any>(null);
    const [submitting, setSubmitting] = useState(false);
    const [showFilters, setShowFilters] = useState(false);

    const { showToast } = useToast();
    const { formatCost } = useCurrency();

    // Modals
    const [showFormModal, setShowFormModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showRenewModal, setShowRenewModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showUnassignModal, setShowUnassignModal] = useState(false);
    // Holders offered by the revoke picker. Empty when revoking a specific
    // assignment from the details modal, which already knows which one.
    const [revokeCandidates, setRevokeCandidates] = useState<any[]>([]);
    const [revokeLicenseName, setRevokeLicenseName] = useState('');
    const [loadingRevoke, setLoadingRevoke] = useState(false);
    const [showAdjustSeatsModal, setShowAdjustSeatsModal] = useState(false);
    const [showImportPreviewModal, setShowImportPreviewModal] = useState(false);
    const [importPreviewData, setImportPreviewData] = useState<ImportPreviewRow[]>([]);
    const [importPreviewSummary, setImportPreviewSummary] = useState<ImportPreviewSummary | undefined>(undefined);
    const [isImporting, setIsImporting] = useState(false);
    const importInputRef = useRef<HTMLInputElement>(null);

    // Selected Items
    const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
    const [confirmState, setConfirmState] = useState<{ show: boolean; title: string; message: string; onConfirm: () => void; type?: 'danger' | 'warning' | 'primary' }>({ show: false, title: '', message: '', onConfirm: () => { } });

    // Action Form States. Ids/counts use 0 as their "empty" sentinel, so their
    // required checks live in `custom` rules, which run on empty values.
    const [renewData, setRenewData] = useState({ newExpiryDate: '', costChange: 0, remarks: '' });
    const assignForm = useForm({ userId: 0, notes: '' }, {
        userId: { label: 'User', custom: (v) => (!v ? 'User is required.' : null) },
    });
    const assignData = assignForm.values;
    const setAssignData = assignForm.setValues;
    const unassignForm = useForm({ assignmentId: 0, reason: '' }, {
        assignmentId: { label: 'Assigned user', custom: (v) => (!v ? 'Select the user to revoke from.' : null) },
        reason: { label: 'Reason for revoking', custom: (v) => (!String(v ?? '').trim() ? 'Reason for revoking is required.' : null) },
    });
    const unassignData = unassignForm.values;
    const adjustSeatsForm = useForm({ seats: 0, usedSeats: 0, reason: '' }, {
        seats: { label: 'Total seat count', custom: (v) => (!v || v <= 0 ? 'Total seat count must be greater than zero.' : null) },
        usedSeats: {
            label: 'Used seat count',
            custom: (v, all) => (v > all.seats ? 'Used seat count cannot exceed the total seat count.' : null),
        },
        reason: { label: 'Adjustment reason', custom: (v) => (!String(v ?? '').trim() ? 'Adjustment reason is required.' : null) },
    });
    const adjustSeatsData = adjustSeatsForm.values;

    /** Fetches the current page for a settled query state — the single
     * fetch trigger the shared hook calls after every user action. The
     * AbortSignal cancels a still-in-flight request from a superseded
     * state, and `licenses` keeps its previous rows visible the whole time
     * (only `listLoading` toggles a dim overlay). */
    const loadLicensesForState = async (
        qState: { tab: string; search: string; filters: LicenseAdvancedFilterState; sortBy?: string; sortDir?: 'asc' | 'desc'; page: number; pageSize: number },
        signal: AbortSignal,
    ) => {
        try {
            setListLoading(true);
            const result = await licenseService.getLicenses({
                ...qState.filters,
                tab: qState.tab as LicenseListFilters['tab'],
                search: qState.search || undefined,
                sortBy: qState.sortBy,
                sortDir: qState.sortDir,
                page: qState.page,
                limit: qState.pageSize,
            }, signal);
            setLicenses(result.data);
            setTotalLicenses(result.total);
        } catch (error: any) {
            if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
            console.error('Failed to load licenses:', error);
            showToast('Failed to load licenses', 'error');
        } finally {
            setListLoading(false);
        }
    };

    const qts = useManagementTableState<LicenseAdvancedFilterState>({
        emptyFilters: EMPTY_LICENSE_FILTERS,
        defaultTab: 'all',
        defaultSortBy: 'softwareName',
        defaultSortDir: 'asc',
        onStateChange: loadLicensesForState,
    });

    /** Builds the LicenseListFilters payload for one-off calls outside the
     * hook's own fetch cycle (CSV export). */
    const buildListFilters = (forExport = false): LicenseListFilters => ({
        ...qts.state.filters,
        tab: qts.state.tab as LicenseListFilters['tab'],
        search: qts.state.search || undefined,
        sortBy: qts.state.sortBy,
        sortDir: qts.state.sortDir,
        ...(forExport ? {} : { page: qts.state.page, limit: qts.state.pageSize }),
    });

    /** Status-tab badge counts + seat utilization — permission-scoped like the
     * list, but intentionally NOT re-filtered by search/advanced filters. */
    const loadStats = async () => {
        try {
            setStats(await licenseService.getStatistics());
        } catch (e) { console.error('Stats fail', e); }
    };

    /** Users, vendors, plans, departments, and lookups — loaded once; used by
     * both the license form and the advanced-filters dropdowns. */
    const loadStaticData = async () => {
        try {
            const usersData = await userService.getUsers();
            setUsers(usersData);
        } catch (e) { console.error('Users fail', e); }

        masterService.getVendors().then(setVendors).catch(e => console.error('Vendors fail', e));
        masterService.getPlans().then(setLicensePlans).catch(e => console.error('Plans fail', e));
        departmentsService.getAll().then(setDepartments).catch(e => console.error('Departments fail', e));
        masterService.getLookups().then(lData => {
            const groupedLookups: Record<string, Lookup[]> = {};
            lData.forEach(l => {
                if (!groupedLookups[l.type]) groupedLookups[l.type] = [];
                groupedLookups[l.type].push(l);
            });
            setLookups(groupedLookups);
        }).catch(e => console.error('Lookups fail', e));
    };

    /** Re-fetches the current page and stats — used after
     * create/update/delete/renew/etc. that don't otherwise change the
     * hook's own tab/search/filter/sort/page state. */
    const loadData = async () => {
        await Promise.all([loadLicensesForState(qts.state, new AbortController().signal), loadStats()]);
    };

    useEffect(() => {
        const init = async () => {
            try {
                const user = await authService.getProfile();
                setCurrentUser(user);
                await Promise.all([loadStaticData(), loadStats()]);
            } catch (error) {
                console.error('Initialization failed:', error);
            } finally {
                setLoading(false);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCreate = () => {
        setSelectedLicense(null);
        setShowFormModal(true);
    };

    const handleEdit = (license: License) => {
        setSelectedLicense(license);
        setShowFormModal(true);
    };

    const handleFormSubmit = async (data: Partial<License>) => {
        try {
            setSubmitting(true);
            if (selectedLicense) {
                await licenseService.updateLicense(selectedLicense.id, data);
                showToast('License updated successfully', 'success');
            } else {
                await licenseService.createLicense(data);
                showToast('License created successfully', 'success');
            }
            setShowFormModal(false);
            loadData();
        } catch (error: any) {
            console.error('Failed to save license:', error);
            showToast(error.response?.data?.message || 'Failed to save license', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (id: number) => {
        setConfirmState({
            show: true, title: 'Delete License', message: 'Are you sure you want to delete this license? Licenses with assignment history cannot be deleted and should be terminated or left to expire instead.', type: 'danger',
            onConfirm: async () => {
                try {
                    await licenseService.deleteLicense(id);
                    showToast('License deleted', 'success');
                    loadData();
                } catch (error: any) {
                    showToast(error?.response?.data?.message || 'Failed to delete', 'error');
                }
                setConfirmState(prev => ({ ...prev, show: false }));
            }
        });
    };

    const handleViewDetails = async (id: number) => {
        try {
            const license = await licenseService.getLicense(id);
            setSelectedLicense(license);
            setShowDetailsModal(true);
        } catch {
            showToast('Failed to load details', 'error');
        }
    };

    // Renewal Actions
    const openRenewModal = (license: License) => {
        setSelectedLicense(license);
        setRenewData({
            newExpiryDate: license.expiryDate ? new Date(license.expiryDate).toISOString().split('T')[0] : '',
            costChange: 0,
            remarks: ''
        });
        setShowRenewModal(true);
    };

    const openAdjustSeatsModal = (license: License) => {
        setSelectedLicense(license);
        adjustSeatsForm.resetForm({
            seats: license.totalSeats,
            usedSeats: license.usedSeats,
            reason: ''
        });
        setShowAdjustSeatsModal(true);
    };

    const submitRenewal = async () => {
        if (!selectedLicense) return;
        try {
            setSubmitting(true);
            await licenseService.renewLicense(selectedLicense.id, renewData);
            showToast('License renewed successfully', 'success');
            setShowRenewModal(false);
            if (showDetailsModal) handleViewDetails(selectedLicense.id); // Refresh details if open
            loadData();
        } catch (error: any) {
            showToast(error.response?.data?.message || 'Failed to renew license', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const submitSeatAdjustment = async () => {
        if (!selectedLicense) return;
        if (!adjustSeatsForm.validateForm()) return;
        try {
            setSubmitting(true);
            await licenseService.adjustSeats(selectedLicense.id, adjustSeatsData);
            showToast('Seats adjusted successfully', 'success');
            setShowAdjustSeatsModal(false);
            if (showDetailsModal) handleViewDetails(selectedLicense.id);
            loadData();
        } catch (error: any) {
            showToast(error.response?.data?.message || 'Failed to adjust seats', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Assignment Handlers (Passed to Details)
    const handleUnassign = (assignmentId: number) => {
        setRevokeCandidates([]);
        setRevokeLicenseName('');
        unassignForm.resetForm({ assignmentId, reason: '' });
        setShowUnassignModal(true);
    };

    /**
     * Revoke from the row menu. A license can have many holders and the API
     * revokes exactly one assignment per call, so the modal has to offer a
     * choice — and the list query does not join assignments, so they are
     * fetched here rather than read off the row.
     */
    const handleOpenRevoke = async (license: License) => {
        setRevokeLicenseName(license.softwareName);
        unassignForm.resetForm({ assignmentId: 0, reason: '' });
        setRevokeCandidates([]);
        setLoadingRevoke(true);
        setShowUnassignModal(true);
        try {
            const full = await licenseService.getLicense(license.id);
            const holders = full.assignments || [];
            setRevokeCandidates(holders);
            // One holder is not a choice — preselect so the form is one step.
            if (holders.length === 1) unassignForm.handleChange('assignmentId', holders[0].id);
        } catch {
            showToast('Failed to load the users holding this license', 'error');
            setShowUnassignModal(false);
        } finally {
            setLoadingRevoke(false);
        }
    };

    const submitUnassign = async () => {
        if (!unassignData.assignmentId) return;
        if (!unassignForm.validateForm()) return;
        try {
            await licenseService.unassignLicense(unassignData.assignmentId, unassignData.reason);
            showToast('License revoked successfully', 'success');
            setShowUnassignModal(false);
            if (selectedLicense) handleViewDetails(selectedLicense.id);
            loadData();
        } catch {
            showToast('Failed to revoke the license', 'error');
        }
    };

    const handleAssign = () => {
        if (selectedLicense) {
            assignForm.resetForm({ userId: 0, notes: '' });
            setShowAssignModal(true);
        }
    };

    const openAssignModal = (license: License) => {
        setSelectedLicense(license);
        assignForm.resetForm({ userId: 0, notes: '' });
        setShowAssignModal(true);
    };

    const submitAssign = async () => {
        if (!selectedLicense) return;
        if (!assignForm.validateForm()) return;
        try {
            await licenseService.assignLicense(selectedLicense.id, assignData.userId, assignData.notes);
            showToast('License assigned successfully', 'success');
            setShowAssignModal(false);
            if (showDetailsModal) handleViewDetails(selectedLicense.id);
            loadData();
        } catch (error: any) {
            showToast(error.response?.data?.message || 'Failed to assign license', 'error');
        }
    };

    const handleExportCSV = async () => {
        let exportLicenses: License[];
        try {
            const result = await licenseService.getLicenses(buildListFilters(true));
            exportLicenses = result.data;
        } catch {
            showToast('Failed to prepare export', 'error');
            return;
        }
        if (exportLicenses.length === 0) {
            showToast('No data to export', 'warning');
            return;
        }

        const headers = ['Software Name', 'Vendor', 'Category', 'Type', 'Total Seats', 'Used Seats', 'Expiry Date', 'Total Cost'];
        const rows = exportLicenses.map(l => [
            l.softwareName,
            l.vendorObj?.name || l.vendor || '',
            l.category,
            l.type,
            l.totalSeats,
            l.usedSeats,
            l.expiryDate || 'Perpetual',
            l.totalCost || (l.unitPrice || 0) * l.totalSeats
        ]);

        // Via the shared helper: this used to join fields with a bare comma,
        // so a software name or vendor containing one ("Microsoft 365, Business
        // Standard") split into extra columns and shifted the rest of the row.
        exportToCsv(
            `licenses_export_${new Date().toISOString().split('T')[0]}.csv`,
            headers,
            rows,
        );
        showToast('Licenses exported successfully', 'success');
    };

    const handleImportClick = () => {
        importInputRef.current?.click();
    };

    const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.xlsx')) {
            showToast('Please upload an Excel (.xlsx) file', 'error');
            return;
        }

        try {
            setLoading(true);
            const result = await licenseService.validateImport(file);
            setImportPreviewData(result.data);
            setImportPreviewSummary(result.summary);
            setShowImportPreviewModal(true);
        } catch (error: any) {
            showToast('Validation failed: ' + (error.response?.data?.message || error.message), 'error');
        } finally {
            setLoading(false);
            if (importInputRef.current) importInputRef.current.value = '';
        }
    };

    const handleConfirmImport = async () => {
        try {
            setIsImporting(true);
            // WARNING rows (possible duplicates) are importable; only ERROR rows
            // are excluded. The backend re-validates and re-checks duplicates on
            // every row it receives regardless.
            const validLicenses = importPreviewData.filter(a => a._status !== 'ERROR');
            if (validLicenses.length === 0) {
                showToast('No valid licenses to import', 'error');
                return;
            }

            const result = await licenseService.confirmImport(validLicenses);
            
            if (result.success > 0) {
                showToast(`Successfully imported ${result.success} licenses`, 'success');
            }
            if (result.failed > 0) {
                showToast(`Failed to import ${result.failed} licenses.`, 'warning');
                console.error('Import Errors:', result.errors);
            }
            
            setShowImportPreviewModal(false);
            loadData();
        } catch (error: any) {
            showToast('Import failed: ' + (error.response?.data?.message || error.message), 'error');
        } finally {
            setIsImporting(false);
        }
    };

    // Status derivation kept client-side for badges/labels; actual filtering,
    // sorting, and pagination now all happen server-side (see loadLicenses) —
    // `licenses` already holds exactly the current page.
    const isExpiringSoon = (d?: string) => { if (!d) return false; const diff = Math.ceil((new Date(d).getTime() - Date.now()) / 86400000); return diff > 0 && diff <= 30; };
    const isExpired = (d?: string) => d ? new Date(d) < new Date() : false;

    const paginatedLicenses = licenses;
    const advancedFilters = qts.state.filters;

    const removeFromArrayFilter = <K extends keyof LicenseAdvancedFilterState>(key: K, value: string | number) => {
        const arr = (advancedFilters[key] as any[] | undefined)?.filter((v) => v !== value);
        qts.setFilters({ ...advancedFilters, [key]: arr && arr.length ? arr : undefined });
    };
    const clearFilterKeys = (...keys: (keyof LicenseAdvancedFilterState)[]) => {
        const next = { ...advancedFilters };
        keys.forEach((k) => { delete next[k]; });
        qts.setFilters(next);
    };

    // Removable chips shown above the table for every active advanced filter.
    const filterChips: FilterChip[] = useMemo(() => {
        const chips: FilterChip[] = [];
        (advancedFilters.category ?? []).forEach(v => chips.push({
            key: `category-${v}`,
            label: `Category: ${lookups['LICENSE_CATEGORY']?.find(l => l.value === v)?.label || v}`,
            onRemove: () => removeFromArrayFilter('category', v),
        }));
        (advancedFilters.type ?? []).forEach(v => chips.push({
            key: `type-${v}`,
            label: `Model: ${lookups['LICENSE_TYPE']?.find(l => l.value === v)?.label || v}`,
            onRemove: () => removeFromArrayFilter('type', v),
        }));
        (advancedFilters.vendor ?? []).forEach(v => chips.push({ key: `vendor-${v}`, label: `Vendor: ${v}`, onRemove: () => removeFromArrayFilter('vendor', v) }));
        (advancedFilters.billingFrequency ?? []).forEach(v => chips.push({
            key: `billing-${v}`,
            label: `Billing: ${lookups['BILLING_FREQUENCY']?.find(l => l.value === v)?.label || v}`,
            onRemove: () => removeFromArrayFilter('billingFrequency', v),
        }));
        (advancedFilters.commitmentTerm ?? []).forEach(v => chips.push({
            key: `commitment-${v}`,
            label: `Term: ${lookups['COMMITMENT_TERM']?.find(l => l.value === v)?.label || v}`,
            onRemove: () => removeFromArrayFilter('commitmentTerm', v),
        }));
        (advancedFilters.renewalStatus ?? []).forEach(v => chips.push({
            key: `renewal-${v}`,
            label: `Renewal: ${lookups['RENEWAL_STATUS']?.find(l => l.value === v)?.label || v}`,
            onRemove: () => removeFromArrayFilter('renewalStatus', v),
        }));
        (advancedFilters.complianceRisk ?? []).forEach(v => chips.push({
            key: `risk-${v}`,
            label: `Risk: ${lookups['COMPLIANCE_RISK']?.find(l => l.value === v)?.label || v}`,
            onRemove: () => removeFromArrayFilter('complianceRisk', v),
        }));
        (advancedFilters.assignedToId ?? []).forEach(id => {
            const u = users.find(u => u.id === id);
            chips.push({ key: `assignedTo-${id}`, label: `Assigned To: ${u ? `${u.firstName} ${u.lastName}` : id}`, onRemove: () => removeFromArrayFilter('assignedToId', id) });
        });
        (advancedFilters.licensePlanId ?? []).forEach(id => {
            const p = licensePlans.find(p => p.id === id);
            chips.push({ key: `plan-${id}`, label: `Plan: ${p?.name || id}`, onRemove: () => removeFromArrayFilter('licensePlanId', id) });
        });
        (advancedFilters.departmentId ?? []).forEach(id => {
            const d = departments.find(d => d.id === id);
            chips.push({ key: `dept-${id}`, label: `Department: ${d?.name || id}`, onRemove: () => removeFromArrayFilter('departmentId', id) });
        });
        if (advancedFilters.purchaseDateFrom || advancedFilters.purchaseDateTo) {
            chips.push({
                key: 'purchaseDateRange',
                label: `Purchase Date: ${advancedFilters.purchaseDateFrom || '…'} – ${advancedFilters.purchaseDateTo || '…'}`,
                onRemove: () => clearFilterKeys('purchaseDateFrom', 'purchaseDateTo'),
            });
        }
        if (advancedFilters.expiryDateFrom || advancedFilters.expiryDateTo) {
            chips.push({
                key: 'expiryDateRange',
                label: `Expiry Date: ${advancedFilters.expiryDateFrom || '…'} – ${advancedFilters.expiryDateTo || '…'}`,
                onRemove: () => clearFilterKeys('expiryDateFrom', 'expiryDateTo'),
            });
        }
        if (advancedFilters.totalCostMin !== undefined || advancedFilters.totalCostMax !== undefined) {
            chips.push({
                key: 'totalCostRange',
                label: `Total Cost: ${advancedFilters.totalCostMin ?? '…'} – ${advancedFilters.totalCostMax ?? '…'}`,
                onRemove: () => clearFilterKeys('totalCostMin', 'totalCostMax'),
            });
        }
        if (advancedFilters.totalSeatsMin !== undefined || advancedFilters.totalSeatsMax !== undefined) {
            chips.push({
                key: 'totalSeatsRange',
                label: `Total Seats: ${advancedFilters.totalSeatsMin ?? '…'} – ${advancedFilters.totalSeatsMax ?? '…'}`,
                onRemove: () => clearFilterKeys('totalSeatsMin', 'totalSeatsMax'),
            });
        }
        if (advancedFilters.utilizationMin !== undefined || advancedFilters.utilizationMax !== undefined) {
            chips.push({
                key: 'utilizationRange',
                label: `Utilization: ${advancedFilters.utilizationMin ?? '…'}% – ${advancedFilters.utilizationMax ?? '…'}%`,
                onRemove: () => clearFilterKeys('utilizationMin', 'utilizationMax'),
            });
        }
        return chips;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [advancedFilters, users, licensePlans, departments, lookups]);

    // Filter-option lists for the drawer — active master-data values only.
    const categoryFilterOptions: MultiSelectOption[] = useMemo(
        () => (lookups['LICENSE_CATEGORY'] || []).filter(l => l.isActive !== false).map(l => ({ value: l.value, label: l.label })),
        [lookups]
    );
    const typeFilterOptions: MultiSelectOption[] = useMemo(
        () => (lookups['LICENSE_TYPE'] || []).filter(l => l.isActive !== false).map(l => ({ value: l.value, label: l.label })),
        [lookups]
    );
    const vendorFilterOptions: MultiSelectOption[] = useMemo(
        () => vendors.filter(v => v.isActive !== false).map(v => ({ value: v.name, label: v.name })),
        [vendors]
    );
    const billingFrequencyFilterOptions: MultiSelectOption[] = useMemo(
        () => (lookups['BILLING_FREQUENCY'] || []).filter(l => l.isActive !== false).map(l => ({ value: l.value, label: l.label })),
        [lookups]
    );
    const commitmentTermFilterOptions: MultiSelectOption[] = useMemo(
        () => (lookups['COMMITMENT_TERM'] || []).filter(l => l.isActive !== false).map(l => ({ value: l.value, label: l.label })),
        [lookups]
    );
    const renewalStatusFilterOptions: MultiSelectOption[] = useMemo(
        () => (lookups['RENEWAL_STATUS'] || []).filter(l => l.isActive !== false).map(l => ({ value: l.value, label: l.label })),
        [lookups]
    );
    const complianceRiskFilterOptions: MultiSelectOption[] = useMemo(
        () => (lookups['COMPLIANCE_RISK'] || []).filter(l => l.isActive !== false).map(l => ({ value: l.value, label: l.label })),
        [lookups]
    );
    const userFilterOptions: MultiSelectOption[] = useMemo(
        () => users.filter(u => u.isActive).map(u => ({ value: String(u.id), label: `${u.firstName} ${u.lastName}` })),
        [users]
    );
    const licensePlanFilterOptions: MultiSelectOption[] = useMemo(
        () => licensePlans.filter(p => p.isActive !== false).map(p => ({ value: String(p.id), label: p.name })),
        [licensePlans]
    );
    const departmentFilterOptions: MultiSelectOption[] = useMemo(
        () => departments.filter(d => d.isActive !== false).map(d => ({ value: String(d.id), label: d.name })),
        [departments]
    );

    const handleDownloadTemplate = async () => {
        try {
            await licenseService.downloadImportTemplate();
        } catch (error: any) {
            showToast('Failed to download template: ' + (error.response?.data?.message || error.message), 'error');
        }
    };

    const getStatusBadge = (license: License) => {
        if (isExpired(license.expiryDate)) return <Badge variant="destructive">Expired</Badge>;
        if (isExpiringSoon(license.expiryDate)) return <Badge variant="warning">Expiring Soon</Badge>;
        return <Badge variant="success">Active</Badge>;
    };

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

    // Badge counts come from the dedicated statistics endpoint (permission-scoped,
    // like the list itself) rather than being derived from the fetched page.
    const tabs = [
        { key: 'all', label: 'All Licenses', icon: <KeyRound className="h-4 w-4" />, count: stats?.total ?? 0 },
        { key: 'active', label: 'Active', icon: <Check className="h-4 w-4" />, count: stats?.active ?? 0 },
        { key: 'expiring', label: 'Expiring Soon', icon: <CalendarClock className="h-4 w-4" />, count: stats?.expiringSoon ?? 0 },
        { key: 'expired', label: 'Expired', icon: <AlertTriangle className="h-4 w-4" />, count: stats?.expired ?? 0 },
    ];

    return (
        <div className="space-y-6">
            <PageHeader title="License Management" description="Track software licenses, subscriptions, and renewals.">
                <div className="flex items-center gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="gap-2">
                                <Settings2 className="h-4 w-4" /> Manage Data
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            <DropdownMenuItem onClick={handleExportCSV} className="gap-2">
                                <FileSpreadsheet className="h-4 w-4 text-success" />
                                Export to Excel (.csv)
                            </DropdownMenuItem>
                            {currentUser?.role?.name === 'Admin' && (
                                <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleDownloadTemplate} className="gap-2">
                                        <Download className="h-4 w-4 text-primary" />
                                        Download Import Template
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={handleImportClick} className="gap-2">
                                        <Upload className="h-4 w-4 text-primary" />
                                        Import Data
                                    </DropdownMenuItem>
                                </>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {currentUser?.role?.name === 'Admin' && (
                        <>
                            <input
                                type="file"
                                ref={importInputRef}
                                className="hidden"
                                accept=".xlsx"
                                onChange={handleFileImport}
                            />
                            <Button onClick={handleCreate}><Plus className="h-4 w-4 mr-2" />Add License</Button>
                        </>
                    )}
                </div>
            </PageHeader>


            <Card>
                <ListTabBar>
                    <Tabs value={qts.state.tab} onValueChange={(v) => qts.setTab(v)}>
                        <TabsList className="bg-transparent h-auto p-0 gap-8 border-none flex justify-start">
                            {tabs.map((tab) => (
                                <TabsTrigger
                                    key={tab.key}
                                    value={tab.key}
                                    className="group relative data-[state=active]:text-primary data-[state=active]:shadow-none rounded-none px-0 pb-3 pt-4 bg-transparent whitespace-nowrap gap-2 text-muted-foreground hover:text-foreground transition-all border-none"
                                >
                                    <div className="flex items-center gap-1.5">
                                        {tab.icon}
                                        <span className="font-semibold text-sm tracking-tight">{tab.label}</span>
                                        <span className="ml-0.5 px-2 py-0.5 rounded-full bg-muted text-[10px] font-bold group-data-[state=active]:bg-primary/10 group-data-[state=active]:text-primary transition-colors">
                                            {tab.count}
                                        </span>
                                    </div>
                                    {qts.state.tab === tab.key && (
                                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary animate-in fade-in slide-in-from-bottom-1" />
                                    )}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </ListTabBar>

                <ListToolbar
                    searchValue={qts.searchInput}
                    onSearchChange={qts.setSearchInput}
                    searchPlaceholder="Search software, vendor..."
                    activeFilterCount={qts.activeFilterCount}
                    onOpenFilters={() => { qts.syncDraft(); setShowFilters(true); }}
                    onClearAll={qts.clearSearchAndFilters}
                    hasActive={qts.hasActiveSearchOrFilters}
                />

                <FilterChipsBar chips={filterChips} onClearAll={qts.clearFilters} />

                <TableLoadingBar show={listLoading} />

                <CardContent className="p-0">
                    {/* Mobile (<md): stacked card list — tap a card for full details */}
                    <div className={cn("md:hidden divide-y divide-border", listLoading && TABLE_DIM_CLASS)}>
                        {paginatedLicenses.length === 0 ? (
                            <EmptyLicensesState hasFilters={qts.hasActiveSearchOrFilters} onClearFilters={qts.clearSearchAndFilters} />
                        ) : (
                            paginatedLicenses.map(license => (
                                <div
                                    key={license.id}
                                    className="flex items-start gap-3 px-4 py-3 active:bg-accent/50 cursor-pointer"
                                    onClick={() => handleViewDetails(license.id)}
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center justify-between gap-2">
                                            <p className="truncate font-semibold text-primary">
                                                {license.licensePlan?.name || license.planName}
                                            </p>
                                            {getStatusBadge(license)}
                                        </div>
                                        <p className="truncate text-xs text-muted-foreground">
                                            {license.softwareName} · {license.vendorObj?.name || license.vendor}
                                        </p>
                                        <div className="mt-2 flex items-center gap-2">
                                            <Progress value={(license.usedSeats / license.totalSeats) * 100} className="h-1.5 flex-1" />
                                            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                                                {license.usedSeats}/{license.totalSeats} seats
                                            </span>
                                        </div>
                                        <p className="mt-1 text-xs text-muted-foreground">
                                            {license.expiryDate
                                                ? `Expires ${new Date(license.expiryDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
                                                : 'Perpetual'}
                                            {' · '}
                                            {formatCost(license.totalCost || (license.unitPrice || 0) * (license.totalSeats || 1), license.currency)}
                                        </p>
                                    </div>
                                    <div className="flex shrink-0 items-center" onClick={(e) => e.stopPropagation()}>
                                        {currentUser?.role?.name === 'Admin' && (
                                            <ActionDropdown
                                                actions={[
                                                    { label: 'Assign', icon: <UserPlus className="h-4 w-4" />, onClick: () => openAssignModal(license), variant: 'default' as const, disabled: license.usedSeats >= license.totalSeats },
                                                    { label: 'Revoke License', icon: <UserMinus className="h-4 w-4" />, onClick: () => handleOpenRevoke(license), variant: 'default' as const, disabled: !license.usedSeats },
                                                    { label: 'Adjust Seats', icon: <Users className="h-4 w-4" />, onClick: () => openAdjustSeatsModal(license), variant: 'default' as const },
                                                    { label: 'Renew', icon: <RefreshCw className="h-4 w-4" />, onClick: () => openRenewModal(license), variant: 'default' as const },
                                                    { label: 'Edit', icon: <Edit className="h-4 w-4" />, onClick: () => handleEdit(license), variant: 'default' as const },
                                                ] as ActionItem[]}
                                            />
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Desktop (md+): full data table */}
                    <div className={cn("hidden md:block", listLoading && TABLE_DIM_CLASS)}>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead onClick={() => qts.toggleSort('softwareName')} className="cursor-pointer">Software / Plan <ArrowUpDown className="inline h-3 w-3" /></TableHead>
                                <TableHead>Vendor</TableHead>
                                <TableHead>Category</TableHead>
                                <TableHead>Licensing Model</TableHead>
                                <TableHead>Seats</TableHead>
                                <TableHead onClick={() => qts.toggleSort('expiryDate')} className="cursor-pointer">Expiry <ArrowUpDown className="inline h-3 w-3" /></TableHead>
                                <TableHead onClick={() => qts.toggleSort('cost')} className="cursor-pointer">Cost <ArrowUpDown className="inline h-3 w-3" /></TableHead>
                                <TableHead>Status</TableHead>
                                {currentUser?.role?.name === 'Admin' && <TableHead className="text-right">Actions</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedLicenses.length === 0 ? (
                                <TableRow><TableCell colSpan={9} className="py-8"><EmptyLicensesState hasFilters={qts.hasActiveSearchOrFilters} onClearFilters={qts.clearSearchAndFilters} /></TableCell></TableRow>
                            ) : (
                                paginatedLicenses.map(license => (
                                    <TableRow key={license.id}>
                                        <TableCell>
                                            <div className="font-semibold text-primary">{license.licensePlan?.name || license.planName}</div>
                                            <div className="text-xs text-muted-foreground mt-0.5">
                                                {license.softwareName}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm">{license.vendorObj?.name || license.vendor}</div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="font-normal">
                                                {lookups['LICENSE_CATEGORY']?.find(l => l.value === license.category)?.label || license.category?.replace(/_/g, ' ')}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm">
                                                {lookups['LICENSE_TYPE']?.find(l => l.value === license.type)?.label || license.type?.replace(/_/g, ' ')}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm py-1">
                                                <div className="flex justify-between text-[11px] mb-1">
                                                    <span>{license.usedSeats} / {license.totalSeats}</span>
                                                    <span className="text-muted-foreground">{formatPercent(license.totalSeats > 0 ? (license.usedSeats / license.totalSeats) * 100 : 0)}</span>
                                                </div>
                                                <Progress value={(license.usedSeats / license.totalSeats) * 100} className="h-1.5 w-24" />
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm font-medium">
                                            {license.expiryDate ? new Date(license.expiryDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : <span className="text-muted-foreground">Perpetual</span>}
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm font-medium">
                                                {formatCost(license.totalCost || (license.unitPrice || 0) * (license.totalSeats || 1), license.currency)}
                                            </div>
                                            <div className="text-[10px] text-muted-foreground uppercase">
                                                {license.unitPrice ? `${formatCost(license.unitPrice, license.currency)}/seat • ` : ''}
                                                {lookups['BILLING_FREQUENCY']?.find(l => l.value === license.billingFrequency)?.label || license.billingFrequency}
                                            </div>
                                        </TableCell>
                                        <TableCell>{getStatusBadge(license)}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end items-center gap-1">
                                                <Button variant="ghost" size="icon-sm" onClick={() => handleViewDetails(license.id)} title="View Details">
                                                    <Eye className="h-4 w-4" />
                                                </Button>
                                                {currentUser?.role?.name === 'Admin' && (
                                                    <ActionDropdown
                                                        actions={[
                                                            { label: 'Assign', icon: <UserPlus className="h-4 w-4" />, onClick: () => openAssignModal(license), variant: 'default' as const, disabled: license.usedSeats >= license.totalSeats },
                                                            { label: 'Revoke License', icon: <UserMinus className="h-4 w-4" />, onClick: () => handleOpenRevoke(license), variant: 'default' as const, disabled: !license.usedSeats },
                                                            { label: 'Adjust Seats', icon: <Users className="h-4 w-4" />, onClick: () => openAdjustSeatsModal(license), variant: 'default' as const },
                                                            { label: 'Renew', icon: <RefreshCw className="h-4 w-4" />, onClick: () => openRenewModal(license), variant: 'default' as const },
                                                            { label: 'Edit', icon: <Edit className="h-4 w-4" />, onClick: () => handleEdit(license), variant: 'default' as const },
                                                            { label: 'Delete', icon: <Trash2 className="h-4 w-4" />, onClick: () => handleDelete(license.id), variant: 'danger' as const, disabled: ((license.assignments && license.assignments.length > 0) || (license.usedSeats && license.usedSeats > 0)) },
                                                        ] as ActionItem[]}
                                                    />
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                    </div>
                    <Pagination
                        currentPage={qts.state.page}
                        totalPages={Math.max(1, Math.ceil(totalLicenses / qts.state.pageSize))}
                        onPageChange={qts.setPage}
                        totalItems={totalLicenses}
                        pageSize={qts.state.pageSize}
                        onPageSizeChange={qts.setPageSize}
                    />
                </CardContent>
            </Card>

            {/* Create / Edit Modal */}
            <Dialog open={showFormModal} onOpenChange={setShowFormModal}>
                {/* Fixed header/footer; LicenseForm owns the scrollable body */}
                <DialogContent className="sm:max-w-[960px] p-0 sm:p-0 gap-0 flex flex-col overflow-hidden">
                    <DialogHeader className="shrink-0 border-b px-6 py-4">
                        <DialogTitle>{selectedLicense ? 'Edit License' : 'New License'}</DialogTitle>
                    </DialogHeader>
                    <LicenseForm
                        initialData={selectedLicense || {}}
                        onSubmit={handleFormSubmit}
                        onCancel={() => setShowFormModal(false)}
                        isSubmitting={submitting}
                    />
                </DialogContent>
            </Dialog>

            {/* Details Modal */}
            <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
                <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
                    {selectedLicense && (
                        <LicenseDetails
                            license={selectedLicense}
                            onClose={() => setShowDetailsModal(false)}
                            onAssign={handleAssign}
                            onRenew={() => openRenewModal(selectedLicense)}
                            onUnassign={handleUnassign}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Renew Modal */}
            <Dialog open={showRenewModal} onOpenChange={setShowRenewModal}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Renew License</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>New Expiry Date</Label>
                            <Input type="date" value={renewData.newExpiryDate} onChange={e => setRenewData({ ...renewData, newExpiryDate: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Total Cost Change — entire license, not per seat (+/-)</Label>
                            <Input type="number" step="0.01" value={renewData.costChange} onChange={e => setRenewData({ ...renewData, costChange: parseFloat(e.target.value) || 0 })} />
                            <p className="text-xs text-muted-foreground">
                                Difference in the <strong>overall cost of the new term</strong> vs the previous term, covering all {selectedLicense?.totalSeats || 1} seat(s).
                                Enter 0 if the price is unchanged, +100 if the whole renewal costs 100 more, -50 if cheaper.
                            </p>
                            {selectedLicense && (
                                <p className="text-xs text-muted-foreground">
                                    Current total cost: <strong>{formatCost(selectedLicense.totalCost || (selectedLicense.unitPrice || 0) * (selectedLicense.totalSeats || 1), selectedLicense.currency)}</strong>
                                    {renewData.costChange !== 0 && (
                                        <> → new term: <strong>{formatCost((Number(selectedLicense.totalCost) || (selectedLicense.unitPrice || 0) * (selectedLicense.totalSeats || 1)) + renewData.costChange, selectedLicense.currency)}</strong></>
                                    )}
                                </p>
                            )}
                        </div>
                        <div className="space-y-2">
                            <Label>Remarks</Label>
                            <Input value={renewData.remarks} onChange={e => setRenewData({ ...renewData, remarks: e.target.value })} placeholder="Renewal notes..." />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRenewModal(false)}>Cancel</Button>
                        <Button onClick={submitRenewal}>Confirm Renewal</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Adjust Seats Modal */}
            <Dialog open={showAdjustSeatsModal} onOpenChange={setShowAdjustSeatsModal}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Adjust License Seats</DialogTitle></DialogHeader>
                    {selectedLicense && (
                        <div className="space-y-4">
                            <div className="bg-muted/50 p-3 rounded-md text-sm">
                                <p className="font-semibold text-primary">{selectedLicense.licensePlan?.name || selectedLicense.planName || selectedLicense.softwareName}</p>
                                <div className="flex justify-between mt-1 text-xs">
                                    <span>Current Total: <b>{selectedLicense.totalSeats}</b></span>
                                    <span>Currently Used: <b>{selectedLicense.usedSeats}</b></span>
                                </div>
                            </div>
                            <FormField id="adjustSeats" label="New Total Seat Count" required error={adjustSeatsForm.errors.seats}>
                                <Input
                                    type="number"
                                    min="1"
                                    value={adjustSeatsData.seats}
                                    onChange={e => adjustSeatsForm.handleChange('seats', parseInt(e.target.value) || 0)}
                                    onBlur={() => adjustSeatsForm.handleBlur('seats')}
                                />
                            </FormField>
                            <FormField id="adjustUsedSeats" label="New Used Seat Count" error={adjustSeatsForm.errors.usedSeats}>
                                <Input
                                    type="number"
                                    min="0"
                                    max={adjustSeatsData.seats}
                                    value={adjustSeatsData.usedSeats}
                                    onChange={e => adjustSeatsForm.handleChange('usedSeats', parseInt(e.target.value) || 0)}
                                    onBlur={() => adjustSeatsForm.handleBlur('usedSeats')}
                                />
                            </FormField>
                            <div className="bg-primary/5 p-2 rounded border border-primary/10 text-xs flex justify-between items-center">
                                <span className="text-muted-foreground font-medium uppercase tracking-wider">Available After Adjustment</span>
                                <span className="text-lg font-bold text-primary">
                                    {Math.max(0, adjustSeatsData.seats - adjustSeatsData.usedSeats)}
                                </span>
                            </div>
                            <FormField id="adjustReason" label="Adjustment Reason" required error={adjustSeatsForm.errors.reason}>
                                <Input
                                    value={adjustSeatsData.reason}
                                    onChange={e => adjustSeatsForm.handleChange('reason', e.target.value)}
                                    onBlur={() => adjustSeatsForm.handleBlur('reason')}
                                    placeholder="e.g. Scaling team, Contract update..."
                                />
                            </FormField>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAdjustSeatsModal(false)}>Cancel</Button>
                        <Button
                            onClick={submitSeatAdjustment}
                            disabled={submitting || (adjustSeatsData.usedSeats > adjustSeatsData.seats) || !adjustSeatsData.reason.trim() || !adjustSeatsData.seats || adjustSeatsData.seats <= 0}
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Update Seats
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Assign Modal */}
            <Dialog open={showAssignModal} onOpenChange={setShowAssignModal}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Assign License</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <FormField id="assign-license-user" label="Select User" required error={assignForm.errors.userId}>
                            <UserSelect
                                users={users}
                                value={assignData.userId || null}
                                onChange={(userId) => assignForm.handleChange('userId', userId ?? 0)}
                            />
                        </FormField>
                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Input value={assignData.notes} onChange={e => setAssignData({ ...assignData, notes: e.target.value })} placeholder="Assignment notes..." />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAssignModal(false)}>Cancel</Button>
                        <Button onClick={submitAssign}>Assign</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Revoke Modal. Opened either from the row menu — which offers a
                choice of holders — or from the details modal, which already
                knows which assignment is being revoked. */}
            <Dialog open={showUnassignModal} onOpenChange={setShowUnassignModal}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Revoke License</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        {revokeLicenseName && (
                            <p className="text-sm font-medium">{revokeLicenseName}</p>
                        )}
                        {loadingRevoke ? (
                            <p className="text-sm text-muted-foreground">Loading assigned users...</p>
                        ) : revokeCandidates.length > 0 ? (
                            <FormField
                                id="revokeAssignmentId"
                                label="Revoke from"
                                required
                                error={unassignForm.errors.assignmentId}
                                hint="The seat is returned to the pool once revoked"
                            >
                                <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border p-1">
                                    {revokeCandidates.map(a => (
                                        <label
                                            key={a.id}
                                            className={cn(
                                                'flex cursor-pointer items-start gap-3 rounded-md p-2 text-sm hover:bg-muted',
                                                unassignData.assignmentId === a.id && 'bg-muted',
                                            )}
                                        >
                                            <input
                                                type="radio"
                                                name="revokeAssignmentId"
                                                className="mt-1"
                                                checked={unassignData.assignmentId === a.id}
                                                onChange={() => unassignForm.handleChange('assignmentId', a.id)}
                                            />
                                            <span className="min-w-0">
                                                <span className="block truncate font-medium">
                                                    {a.user?.firstName} {a.user?.lastName}
                                                </span>
                                                <span className="block truncate text-xs text-muted-foreground">
                                                    {a.user?.email}
                                                    {a.assignedAt ? ` · assigned ${new Date(a.assignedAt).toLocaleDateString()}` : ''}
                                                </span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </FormField>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                Revoking this license returns the seat to the pool. Please provide a reason for tracking.
                            </p>
                        )}
                        <FormField id="unassignReason" label="Reason for revoking" required error={unassignForm.errors.reason}>
                            <Input
                                value={unassignData.reason}
                                onChange={e => unassignForm.handleChange('reason', e.target.value)}
                                onBlur={() => unassignForm.handleBlur('reason')}
                                placeholder="e.g. Employee left company, License no longer needed..."
                                autoFocus
                            />
                        </FormField>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowUnassignModal(false)}>Cancel</Button>
                        <Button
                            variant="destructive"
                            onClick={submitUnassign}
                            disabled={loadingRevoke || !unassignData.reason || !unassignData.assignmentId}
                        >
                            Revoke
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmModal show={confirmState.show} title={confirmState.title} message={confirmState.message} type={confirmState.type} onConfirm={confirmState.onConfirm} onCancel={() => setConfirmState(prev => ({ ...prev, show: false }))} />

            <LicenseImportPreview
                open={showImportPreviewModal}
                onOpenChange={setShowImportPreviewModal}
                rows={importPreviewData}
                summary={importPreviewSummary}
                isImporting={isImporting}
                onConfirm={handleConfirmImport}
            />

            <LicenseAdvancedFilters
                open={showFilters}
                onOpenChange={setShowFilters}
                draft={qts.draftFilters}
                onDraftChange={qts.setDraftFilters}
                onApply={qts.applyDraft}
                onClear={qts.clearFilters}
                categoryOptions={categoryFilterOptions}
                typeOptions={typeFilterOptions}
                vendorOptions={vendorFilterOptions}
                billingFrequencyOptions={billingFrequencyFilterOptions}
                commitmentTermOptions={commitmentTermFilterOptions}
                renewalStatusOptions={renewalStatusFilterOptions}
                complianceRiskOptions={complianceRiskFilterOptions}
                userOptions={userFilterOptions}
                licensePlanOptions={licensePlanFilterOptions}
                departmentOptions={departmentFilterOptions}
            />
        </div>
    );
};

export default LicenseManagement;
